import React, { useMemo, useState } from 'react';
import Icon from '../../ui/Icon';
import { PageSkeleton } from '../../ui/primitives';
import { OrderStockStatus, plural, stockErrorText, useStockOverview } from '../../magazyn/api';
import { StockTagPill } from './shared';

/*
 * Magazyn → Zamówienia: znacznik zapasu przy każdym zamówieniu z zapotrzebowania.
 * Kolejność jak z API (priorytet kolejki), filtr po statusie.
 */

type Filter = 'all' | OrderStockStatus;

export default function OrderTagsTab() {
  const { data: ov, isLoading, isError, error } = useStockOverview();
  const [filter, setFilter] = useState<Filter>('all');

  const orders = useMemo(() => ov?.orders ?? [], [ov]);
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: orders.length, ok: 0, partial: 0, short: 0 };
    orders.forEach((o) => { c[o.status] += 1; });
    return c;
  }, [orders]);

  if (isLoading) return <PageSkeleton table />;
  if (isError || !ov) {
    return (
      <div className="card"><div className="empty">
        <div className="ei"><Icon name="circle-alert" size={26} /></div>
        <h3>Nie udało się wczytać zapotrzebowania</h3>
        <p>{stockErrorText(error)}</p>
      </div></div>
    );
  }

  const labels = ov.config.tagLabels;
  const list = filter === 'all' ? orders : orders.filter((o) => o.status === filter);
  const fdefs: [Filter, string][] = [['all', 'Wszystkie'], ['short', labels.short], ['partial', labels.partial], ['ok', labels.ok]];

  return (
    <div className="grid" style={{ gap: 14 }}>
      {!ov.demandAvailable && (
        <div className="mga-note warn">
          <Icon name="triangle-alert" size={16} />
          <div><b>UCS chwilowo nie odpowiada</b> — stan magazynu działa, ale zapotrzebowanie zamówień nie jest teraz znane.{ov.demandError ? <span className="muted"> ({ov.demandError})</span> : null}</div>
        </div>
      )}
      {ov.demandAvailable && !ov.stockKnown && (
        <div className="mga-note">
          <Icon name="sunrise" size={16} />
          <div><b>Od resetu nic jeszcze nie wpisano.</b> Znaczniki przy zamówieniach w kiosku pojawią się po wpisaniu pierwszej dostawy — tutaj widać je już teraz, liczone od zera.</div>
        </div>
      )}
      {ov.demandAvailable && ov.stockKnown && !ov.config.showOrderTags && (
        <div className="mga-note">
          <Icon name="eye-off" size={16} />
          <div>Znaczniki są wyłączone w ustawieniach — operatorzy ich nie widzą. Ta lista pokazuje je tylko w panelu.</div>
        </div>
      )}

      <div className="card">
        <div className="mga-toolbar">
          <div className="chips">
            {fdefs.map(([id, lbl]) => (
              <button key={id} className={`chip mga-chip-${id}`} aria-pressed={filter === id} onClick={() => setFilter(id)}>
                {id !== 'all' && <span className={`mga-dot ${id}`} />}
                {lbl}<span className="c">{counts[id]}</span>
              </button>
            ))}
          </div>
          <span className="mga-toolbar-end muted" style={{ fontSize: 13 }}>
            {ov.demandScope === 'all' ? 'Wszystkie otwarte zamówienia' : 'Kolejka na teraz'} · kolejność jak w kiosku
          </span>
        </div>

        <div className="tablewrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Zamówienie</th>
                <th style={{ width: 170 }}>Zapas</th>
                <th style={{ width: 170 }}>Pokrycie</th>
                <th>Brakuje</th>
              </tr>
            </thead>
            <tbody>
              {list.map((o) => {
                const missingUnits = o.missing.reduce((s, m) => s + m.quantity, 0);
                const covered = Math.max(0, o.units - missingUnits);
                const pct = o.units ? Math.round((covered / o.units) * 100) : 100;
                return (
                  <tr key={o.orderId}>
                    <td className="mono" style={{ fontWeight: 600 }}>{o.orderName}</td>
                    <td><StockTagPill tag={o} /></td>
                    <td>
                      <div className="mga-cover">
                        <div className={`mga-bar ${o.status}`}><i style={{ width: `${pct}%` }} /></div>
                        <span className="num mga-cover-n">{covered}/{o.units}</span>
                      </div>
                    </td>
                    <td>
                      {o.missing.length === 0
                        ? <span className="muted" style={{ fontSize: 13 }}>—</span>
                        : (
                          <div className="mga-missing">
                            {o.missing.map((m) => (
                              <span key={m.key} className={`mga-miss ${o.status}`}>
                                <b className="num">{m.quantity}×</b> {m.name}
                              </span>
                            ))}
                          </div>
                        )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {list.length === 0 && (
          <div className="empty" style={{ padding: '40px 16px' }}>
            <div className="ei"><Icon name={orders.length ? 'filter' : 'clipboard-check'} size={26} /></div>
            <h3>{orders.length ? 'Brak zamówień w tym filtrze' : 'Brak zamówień z produktami z magazynu'}</h3>
            <p>{orders.length ? 'Wybierz inny status.' : 'Gdy w kolejce pojawią się zamówienia ze śledzonym pieczywem, zobaczysz je tutaj.'}</p>
          </div>
        )}
        {list.length > 0 && (
          <div className="mga-foot">
            <Icon name="info" size={14} />
            {counts.ok} z {counts.all} {plural(counts.all, ['zamówienia', 'zamówień', 'zamówień'])} można spakować od ręki.
            {ov.config.allocation === 'whole' ? ' Zapas przydzielany najpierw kompletnym zamówieniom.' : ' Zapas przydzielany po kolei wg priorytetu.'}
          </div>
        )}
      </div>
    </div>
  );
}
